// src/features/media/useMergeJobPolling.ts

import { useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useJobData } from '@/shared/hooks/useJobData';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { useMergeTrackingStore, MergeToast, type PendingMerge } from './stores/mergeTrackingStore';
import type { JobData } from '@/lib/types/Job';

/**
 * Consumer of the shared useJobData hook for merge progress tracking.
 *
 * MUST be mounted exactly once, at the AppShell root — NOT in a page.
 * Merge toasts live in the global notification provider and only this hook
 * resolves them (green auto-close on completion); when it lived in
 * VideosPage, navigating away stopped the polling and stranded permanent
 * "Merging..." spinners. Pending merges come from useMergeTrackingStore
 * (registered by VideosPage's merge success handler).
 *
 * Filters mergeJobs by mediaId matching pending merge targets.
 * Uses stale job dedup (snapshot completed IDs on first poll) to avoid
 * reacting to historical jobs — matches usePosterJobPolling pattern.
 *
 * Polling auto-disables when pendingMerges is empty.
 */
export function useMergeJobPolling(): void {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const pendingMerges = useMergeTrackingStore((s) => s.pendingMerges);
  const enabled = pendingMerges.length > 0;
  const { data: jobData, dataUpdatedAt } = useJobData(selectedCId, enabled);

  // Stale job dedup: snapshot completed job IDs on first poll
  const staleJobIds = useRef<Set<number>>(new Set());
  const initializedRef = useRef(false);
  // Track which merges we've already shown terminal notifications for
  const notifiedTerminalRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!jobData || !enabled) {
      if (!enabled) {
        initializedRef.current = false;
        staleJobIds.current.clear();
        notifiedTerminalRef.current.clear();
      }
      return;
    }

    const { mergeJobs } = jobData;

    // Initialize stale job set on first poll
    if (!initializedRef.current) {
      initializedRef.current = true;
      for (const job of mergeJobs) {
        if (job.jobStatus === 'completed' || job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
          staleJobIds.current.add(job.jobId);
        }
      }
    }

    // Build pending mediaId set for fast lookup
    const pendingByMediaId = new Map<number, PendingMerge>();
    for (const pm of pendingMerges) {
      pendingByMediaId.set(pm.mediaId, pm);
    }

    // Match merge jobs to pending merges
    for (const job of mergeJobs) {
      if (staleJobIds.current.has(job.jobId)) continue;
      const jobMediaId = job.ids.mediaId;
      if (jobMediaId == null) continue;

      const pending = pendingByMediaId.get(jobMediaId);
      if (!pending) continue;

      handleMergeJobUpdate(job, pending);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, enabled]);

  function handleMergeJobUpdate(job: JobData, pending: PendingMerge): void {
    const notifId = MergeToast.id(pending.mediaId);

    if (job.jobStatus === 'completed') {
      if (notifiedTerminalRef.current.has(pending.mediaId)) return;
      notifiedTerminalRef.current.add(pending.mediaId);

      notifications.update({
        id: notifId,
        title: 'Merge Complete',
        message: `"${pending.title}" is ready.`,
        color: 'green',
        autoClose: 5000,
        position: 'bottom-right',
      });

      // Refresh video list to show completed merged video
      if (selectedCId != null) {
        queryClient.invalidateQueries({
          queryKey: MEDIA_QUERY_KEYS.videos(selectedCId),
        });
      }

      useMergeTrackingStore.getState().removeMerge(pending.mediaId);
    } else if (job.jobStatus === 'failed' || job.jobStatus === 'canceled') {
      if (notifiedTerminalRef.current.has(pending.mediaId)) return;
      notifiedTerminalRef.current.add(pending.mediaId);

      notifications.update({
        id: notifId,
        title: 'Merge Failed',
        message: `Merge for "${pending.title}" ${job.jobStatus}.`,
        color: 'red',
        autoClose: 8000,
        position: 'bottom-right',
      });

      useMergeTrackingStore.getState().removeMerge(pending.mediaId);
    } else if (job.jobStatus === 'started') {
      notifications.update({
        id: notifId,
        title: 'Merging...',
        message: `"${pending.title}" — ${job.completedRate}%`,
        color: 'blue',
        loading: true,
        autoClose: false,
        position: 'bottom-right',
      });
    }
  }
}
